import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { fetchImmigrationCategories } from '../api';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';

const CATEGORY_META = {
  economic: {
    number: 1,
    label: 'Economic immigrant',
    description:
      "Selected for their ability to contribute to Canada's economy — through labour market needs, owning or managing a business, substantial investment, self-employment, or provincial/territorial programs.",
    color: '#1D9E75',
  },
  family: {
    number: 2,
    label: 'Immigrant sponsored by family',
    description:
      'Sponsored by a Canadian citizen or permanent resident based on their relationship as spouse, partner, parent, grandparent, child, or other relative. Also called family class or family reunification.',
    color: '#378ADD',
  },
  refugee: {
    number: 3,
    label: 'Refugee',
    description:
      'Granted permanent resident status based on a well-founded fear of returning to their home country — including Geneva Convention refugees and persons affected by civil war, armed conflict, or massive human rights violations.',
    color: '#C8873A',
  },
  other: {
    number: 4,
    label: 'Other immigrant',
    description:
      'Granted permanent resident status under a program that does not fall within economic, family-sponsored, or refugee categories.',
    color: '#6B7280',
  },
};

function fmt(n) {
  return Number(n).toLocaleString('en-CA');
}

function fmtShort(n) {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${Math.round(n / 1000)}k`;
  return String(n);
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + (p.value || 0), 0);
  return (
    <div style={{
      background: '#1a1a2e',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: 6,
      padding: '10px 14px',
      fontSize: 13,
      color: '#e8e4da',
      minWidth: 180,
    }}>
      <div style={{ fontWeight: 600, marginBottom: 8, color: '#e8e4da' }}>{label}</div>
      {[...payload].reverse().map(p => (
        <div key={p.dataKey} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 3 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: p.fill, display: 'inline-block' }} />
            <span style={{ color: 'rgba(232,228,218,0.7)' }}>
              {CATEGORY_META[p.dataKey]?.label.split(' ')[0]}
            </span>
          </span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(p.value)}</span>
        </div>
      ))}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ color: 'rgba(232,228,218,0.7)' }}>Total</span>
        <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmt(total)}</span>
      </div>
    </div>
  );
};

export default function Immigration() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchImmigrationCategories()
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, []);

  const latest = data?.latest;
  const categories = latest?.categories || {};
  const currentYear = new Date().getFullYear();
  const ytdRow = data?.history?.find(h => h.year === currentYear);

  // Last 5 complete years for chart (exclude current partial year)
  const chartData = data?.history
    ? [...data.history]
        .filter(h => h.year < currentYear)
        .slice(-5)
        .map(h => ({
          year: String(h.year),
          economic: h.economic,
          family: h.family,
          refugee: h.refugee,
          other: h.other,
        }))
    : [];

  return (
    <div className="min-h-screen bg-cream-100 text-ink-700">
      <Header />

      {/* Banner */}
      <section className="bg-ink-800 text-cream-100">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-16">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-[13px] text-cream-100/65 hover:text-copper-500 mb-6 transition"
          >
            <ArrowLeft size={14} /> Home
          </Link>
          <div className="kicker text-copper-500 mb-4">Immigration</div>
          <h1 className="font-serif text-[56px] md:text-[64px] leading-[0.98] tracking-tight">
            Immigration to Canada
          </h1>
          <p className="mt-6 text-[16px] text-cream-100/75 max-w-2xl leading-relaxed">
            A full picture of immigration to Canada — permanent residents, temporary foreign workers,
            international students, and refugee claimants. Figures drawn from IRCC annual reports
            and Statistics Canada, covering admissions by category, country of origin, and province
            of destination.
          </p>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-6 lg:px-12 py-16">

        {/* Loading skeleton */}
        {loading && (
          <div className="bg-cream-50 border border-ink-300/60 rounded-sm p-12 md:p-16 mb-8 animate-pulse">
            <div className="h-4 w-40 bg-ink-200 rounded mb-6" />
            <div className="h-20 w-64 bg-ink-200 rounded" />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-cream-50 border border-ink-300/60 rounded-sm p-12 mb-8 text-ink-500">
            Could not load live data — please try again shortly.
          </div>
        )}

        {/* 2026 YTD card */}
        {ytdRow && (
          <div className="bg-cream-50 border border-ink-300/60 rounded-sm p-12 md:p-16 mb-4">
            <div className="kicker text-copper-600 mb-6">
              permanent residents admitted · {ytdRow.year} YTD
            </div>
            <div className="font-serif text-[72px] md:text-[100px] leading-[0.9] tracking-tight text-ink-800 tabular">
              {fmt(ytdRow.total)}
            </div>
          </div>
        )}

        {/* Latest complete year card */}
        {latest && (
          <div className="bg-cream-50 border border-ink-300/60 rounded-sm p-12 md:p-16 mb-8">
            <div className="kicker text-copper-600 mb-6">
              permanent residents admitted · {latest.year}
            </div>
            <div className="font-serif text-[72px] md:text-[100px] leading-[0.9] tracking-tight text-ink-800 tabular">
              {fmt(latest.total)}
            </div>
          </div>
        )}

        {/* Stacked bar chart — last 5 years */}
        {chartData.length > 0 && (
          <div className="bg-ink-800 rounded-sm p-8 md:p-10 mb-8">
            <h2 className="font-serif text-[22px] text-cream-100 mb-2">
              Admissions by category
            </h2>
            <p className="text-[13px] text-cream-100/50 mb-8">Last 5 complete years</p>

            <ResponsiveContainer width="100%" height={320}>
              <BarChart
                data={chartData}
                margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                barCategoryGap="28%"
              >
                <CartesianGrid vertical={false} stroke="rgba(255,255,255,0.08)" strokeDasharray="" />
                <XAxis
                  dataKey="year"
                  tick={{ fill: 'rgba(232,228,218,0.6)', fontSize: 13 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={fmtShort}
                  tick={{ fill: 'rgba(232,228,218,0.4)', fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  width={44}
                  tickCount={13}
                  interval={0}
                  ticks={[0,50000,100000,150000,200000,250000,300000,350000,400000,450000,500000,550000,600000]}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                <Bar dataKey="economic" stackId="a" fill={CATEGORY_META.economic.color} radius={[0,0,0,0]} />
                <Bar dataKey="family"   stackId="a" fill={CATEGORY_META.family.color}   radius={[0,0,0,0]} />
                <Bar dataKey="refugee"  stackId="a" fill={CATEGORY_META.refugee.color}   radius={[0,0,0,0]} />
                <Bar dataKey="other"    stackId="a" fill={CATEGORY_META.other.color}     radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>

            {/* Legend */}
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-6">
              {Object.entries(CATEGORY_META).map(([key, meta]) => (
                <div key={key} className="flex items-center gap-2">
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: meta.color, display: 'inline-block', flexShrink: 0 }} />
                  <span className="text-[12px] text-cream-100/60">{meta.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Category breakdown */}
        {latest && (
          <>
            <h2 className="font-serif text-[28px] text-ink-800 mb-6 mt-12">By category · {latest.year}</h2>
            <div className="flex flex-col gap-4">
              {Object.entries(CATEGORY_META).map(([key, meta]) => {
                const cat = categories[key];
                if (!cat) return null;
                return (
                  <div
                    key={key}
                    className="bg-cream-50 border border-ink-300/60 rounded-sm p-8"
                    style={{ borderLeft: `4px solid ${meta.color}` }}
                  >
                    <div className="flex items-start gap-4">
                      <span
                        className="text-[11px] font-semibold rounded px-2 py-0.5 mt-0.5 shrink-0"
                        style={{ color: meta.color, background: meta.color + '18' }}
                      >
                        {meta.number}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 mb-2">
                          <span className="font-semibold text-[15px] text-ink-800">{meta.label}</span>
                          <span className="font-serif text-[24px] text-ink-800 tabular">{fmt(cat.count)}</span>
                        </div>
                        <div className="h-[3px] bg-ink-200 rounded-full overflow-hidden mb-3">
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${cat.pct}%`, background: meta.color }}
                          />
                        </div>
                        <p className="text-[13px] text-ink-500 leading-relaxed mb-2">
                          {meta.description}
                        </p>
                        <p className="text-[12px] text-ink-400">
                          {cat.pct}% of {latest.year} admissions
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Historical trend table */}
        {data?.history?.length > 0 && (
          <>
            <h2 className="font-serif text-[28px] text-ink-800 mb-6 mt-12">Historical admissions</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b border-ink-300/60 text-ink-400 text-left">
                    <th className="pb-3 font-medium">Year</th>
                    <th className="pb-3 font-medium text-right">Total</th>
                    <th className="pb-3 font-medium text-right">Economic</th>
                    <th className="pb-3 font-medium text-right">Family</th>
                    <th className="pb-3 font-medium text-right">Refugee</th>
                    <th className="pb-3 font-medium text-right">Other</th>
                  </tr>
                </thead>
                <tbody>
                  {[...data.history].reverse().map(row => (
                    <tr
                      key={row.year}
                      className={`border-b border-ink-200/60 ${row.year === latest?.year ? 'font-semibold text-ink-800' : 'text-ink-600'}`}
                    >
                      <td className="py-2.5">
                        {row.year}
                        {row.year === currentYear && (
                          <span className="ml-2 text-[11px] text-copper-600 font-medium">YTD</span>
                        )}
                      </td>
                      <td className="py-2.5 text-right tabular">{fmt(row.total)}</td>
                      <td className="py-2.5 text-right tabular">{fmt(row.economic)}</td>
                      <td className="py-2.5 text-right tabular">{fmt(row.family)}</td>
                      <td className="py-2.5 text-right tabular">{fmt(row.refugee)}</td>
                      <td className="py-2.5 text-right tabular">{fmt(row.other)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Source */}
        <div className="mt-10 text-[14px] text-ink-500 leading-relaxed">
          <strong className="text-ink-700">Source:</strong> Immigration, Refugees and Citizenship
          Canada and Statistics Canada (Table 17-10-0040-01).{' '}
          <a
            href="https://open.canada.ca/data/en/dataset/f7e5498e-0ad8-4417-85c9-9b8aff9b9eda"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-copper-600 hover:text-copper-500 ml-1"
          >
            IRCC Open Data <ExternalLink size={11} />
          </a>
        </div>
        <div className="mt-4 text-[13px] text-ink-400 italic max-w-2xl">
          Data refreshes automatically every 6 hours from IRCC's published open data.
        </div>
      </main>

      <Footer />
    </div>
  );
}
